import { newVaultElement } from "@p0security/integrations-vaulted-credential-shared/components";
import {
  Element,
  InstallSpec,
  ItemComponent,
} from "@p0security/shared/install/types";

import { JiraAiConnectorHosting } from "./connector-hosting.ts";
import {
  INVALID_ITEM_ID,
  INVALID_SITE_ID,
  isItemId,
  isSiteId,
} from "./item-id.ts";
import { INVALID_GCP_SECRET_NAME, isGcpSecretName } from "./secret-name.ts";
import { INVALID_SITE_URL, isSiteUrl } from "./site-url.ts";

const itemIdValidator = async (id: string) =>
  isItemId(id) ? undefined : INVALID_ITEM_ID;

const allVaults = newVaultElement({ owner: "Jira site" });

// The beta runs on Google Cloud only: the connector reads and writes secrets
// as its Cloud Run service account, which only Google Secret Manager accepts.
// pagerduty-ai hides it the same way, in pagerduty-ai/shared/src/components.ts.
const vault = Element({
  ...allVaults,
  default: "gcp-sm",
  options: {
    ...allVaults.options,
    "aws-sm": { ...allVaults.options["aws-sm"], hidden: true },
  },
});

/** Three items, each pointing at its parent, so one connector can serve
 * several sites and a site can lease several pool accounts.
 *
 * - `connector`: one Cloud Run deployment of P0's Jira (AI) connector.
 * - `site`: a Jira Cloud site, the connector that reaches it, and the admin
 *   service account's OAuth client the connector manages project roles with.
 * - `pool-account`: one Atlassian service account that P0 leases to agent
 *   sessions on a site, and where its API key is.
 */
export const JiraAiComponents = InstallSpec({
  connector: ItemComponent({
    description: "Runs P0's Jira connector in your Google Cloud project",
    label: "Jira connector",
    maturity: "beta",
    schema: { hosting: JiraAiConnectorHosting },
    type: "string",
    validator: itemIdValidator,
  }),
  site: ItemComponent({
    description:
      "A Jira Cloud site whose projects P0 can give agents access to",
    label: "Jira site",
    maturity: "beta",
    // Every field is fixed at creation. An open request's revoke reads what
    // its stage recorded, not the site, so a changed site would leave nothing
    // to clean up. A new value takes a new site.
    schema: {
      // A `connector` item's id.
      connector: Element({
        description: "The Jira connector that reaches this site",
        label: "Connector",
        step: "new",
        type: "dynamic",
      }),
      siteUrl: Element({
        description: "The address of your Jira Cloud site",
        label: "Site URL",
        placeholder: "https://your-site.atlassian.net",
        step: "new",
        type: "string",
        validator: async (_id, url) =>
          isSiteUrl(url) ? undefined : INVALID_SITE_URL,
      }),
      cloudId: Element({
        description: "Atlassian's ID for the site, which P0 looks up",
        label: "Cloud ID",
        type: "generated",
      }),
      vault,
      adminClientId: Element({
        description:
          "The client ID of the admin service account's OAuth credential",
        label: "Admin client ID",
        step: "new",
        type: "string",
      }),
      adminSecretName: Element({
        description:
          "The name of the secret, in the secret manager above, that holds the admin client's secret as its whole value. P0 never reads it. Only your connector does.",
        label: "Admin client secret name",
        step: "new",
        type: "string",
        validator: async (_id, name) =>
          isGcpSecretName(name) ? undefined : INVALID_GCP_SECRET_NAME,
      }),
    },
    type: "string",
    validator: async (id) => (isSiteId(id) ? undefined : INVALID_SITE_ID),
  }),
  "pool-account": ItemComponent({
    description:
      "An Atlassian service account that P0 gives to one agent session at a time",
    label: "Agent account",
    maturity: "beta",
    schema: {
      // A `site` item's id.
      site: Element({
        description: "The Jira site this account works on",
        label: "Site",
        step: "new",
        type: "dynamic",
        // It becomes part of the account's secret name.
        validator: async (_id, site) =>
          isSiteId(site) ? undefined : INVALID_SITE_ID,
      }),
      email: Element({
        description: "The service account's email, as Atlassian shows it",
        label: "Email",
        step: "new",
        type: "string",
      }),
      accountId: Element({
        description: "Atlassian's ID for the account, which P0 looks up",
        label: "Account ID",
        type: "generated",
      }),
      secretName: Element({
        description:
          "The secret, in the site's secret manager, that holds the service account's API key. P0 names it.",
        label: "API key secret name",
        type: "generated",
      }),
    },
    type: "string",
    validator: itemIdValidator,
  }),
});
